import { useState, useEffect, useCallback, useRef } from 'react';
import api from 'services/api';
import { useTenant } from 'context/TenantContext';
import { exportPagePdf } from 'utils/exportPdf';
import IAAnalysisPanel from 'components/shared/IAAnalysisPanel';
import { aujourdHui } from '../../../../utils/jour';

export default function BalancePage() {
  // Identite du TENANT pour l'en-tete/pied des exports PDF.
  // Sans raison sociale, NIF et RCCM, l'export est REFUSE (cf. utils/exportPdf.js).
  const tenant = useTenant();
  const [data, setData] = useState({ comptes: [], totalMvtDebit: 0, totalMvtCredit: 0, totalSoldeDebit: 0, totalSoldeCredit: 0 });
  const [loading, setLoading] = useState(true);
  const [debut, setDebut] = useState(() => { const d = new Date(); return `${d.getFullYear()}-01-01`; });
  const [fin, setFin] = useState(() => aujourdHui());
  const printRef = useRef();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/syscohada/balance?debut=${debut}&fin=${fin}`);
      const comptes = r.data.comptes || [];
      let totalMvtDebit = 0, totalMvtCredit = 0, totalSoldeDebit = 0, totalSoldeCredit = 0;
      comptes.forEach(c => {
        totalMvtDebit += Number(c.mvt_debit) || 0;
        totalMvtCredit += Number(c.mvt_credit) || 0;
        totalSoldeDebit += Number(c.solde_debiteur) || 0;
        totalSoldeCredit += Number(c.solde_crediteur) || 0;
      });
      setData({ comptes, totalMvtDebit, totalMvtCredit, totalSoldeDebit, totalSoldeCredit });
    } catch {} finally { setLoading(false); }
  }, [debut, fin]);

  useEffect(() => { load(); }, [load]);

  const hasData = data.comptes.length > 0;
  const equilibre = !hasData || Math.abs(data.totalSoldeDebit - data.totalSoldeCredit) < 0.01;

  return (
    <div>
      <div className="gss-toolbar" style={{ marginBottom: 16 }}>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <input type="date" className="gss-input" value={debut} onChange={e => setDebut(e.target.value)} style={{ width: 150 }} />
          <input type="date" className="gss-input" value={fin} onChange={e => setFin(e.target.value)} style={{ width: 150 }} />
        </div>
        <button className="gss-btn gss-btn-primary" onClick={() => exportPagePdf(`Balance générale — ${debut} au ${fin}`, printRef.current, tenant)}>Exporter PDF</button>
      </div>

      <div className="stats-grid stats-grid--4">
        <div className="stat-card">
          <div className="stat-card-header"><span className="stat-card-label">Comptes mouvementés</span></div>
          <div className="stat-card-value">{data.comptes.length}</div>
        </div>
        <div className="stat-card">
          <div className="stat-card-header"><span className="stat-card-label">Total mvt débit</span></div>
          <div className="stat-card-value" style={{ color: '#1565c0' }}>{Number(data.totalMvtDebit).toLocaleString()}</div>
          <div className="stat-card-sub">FCFA</div>
        </div>
        <div className="stat-card">
          <div className="stat-card-header"><span className="stat-card-label">Total mvt crédit</span></div>
          <div className="stat-card-value" style={{ color: '#2e7d32' }}>{Number(data.totalMvtCredit).toLocaleString()}</div>
          <div className="stat-card-sub">FCFA</div>
        </div>
        <div className="stat-card">
          <div className="stat-card-header"><span className="stat-card-label">Équilibre</span></div>
          <div className="stat-card-value" style={{ color: !hasData ? '#888' : equilibre ? '#2e7d32' : '#DC2626', fontSize: 16 }}>
            {!hasData ? 'Aucune donnée' : equilibre ? 'Équilibré' : 'Déséquilibre'}
          </div>
        </div>
      </div>

      <div className="content-card" ref={printRef}>
        <div className="content-card-title">Balance générale</div>
        {loading ? (
          <p style={{ textAlign: 'center', padding: '2rem', color: 'var(--gss-text-light)' }}>Chargement...</p>
        ) : data.comptes.length === 0 ? (
          <p style={{ textAlign: 'center', padding: '2rem', color: '#888' }}>Aucun mouvement sur cette période</p>
        ) : (
          <div className="gss-table-wrap">
            <table className="gss-table">
              <thead>
                <tr>
                  <th style={{ width: 80 }}>N° compte</th>
                  <th>Libellé</th>
                  <th style={{ textAlign: 'right' }}>Mvt débit</th>
                  <th style={{ textAlign: 'right' }}>Mvt crédit</th>
                  <th style={{ textAlign: 'right' }}>Solde débit</th>
                  <th style={{ textAlign: 'right' }}>Solde crédit</th>
                </tr>
              </thead>
              <tbody>
                {data.comptes.map(c => (
                  <tr key={c.numero}>
                    <td style={{ fontWeight: 700, fontFamily: 'monospace' }}>{c.numero}</td>
                    <td>{c.libelle}</td>
                    <td style={{ textAlign: 'right', color: c.mvt_debit > 0 ? '#1565c0' : '#ccc' }}>{c.mvt_debit > 0 ? Number(c.mvt_debit).toLocaleString() : '-'}</td>
                    <td style={{ textAlign: 'right', color: c.mvt_credit > 0 ? '#2e7d32' : '#ccc' }}>{c.mvt_credit > 0 ? Number(c.mvt_credit).toLocaleString() : '-'}</td>
                    <td style={{ textAlign: 'right', fontWeight: 600, color: '#1565c0' }}>{c.solde_debit > 0 ? Number(c.solde_debit).toLocaleString() : '-'}</td>
                    <td style={{ textAlign: 'right', fontWeight: 600, color: '#2e7d32' }}>{c.solde_credit > 0 ? Number(c.solde_credit).toLocaleString() : '-'}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr style={{ fontWeight: 700, borderTop: '2px solid #8B1A1A' }}>
                  <td colSpan={2}>TOTAUX</td>
                  <td style={{ textAlign: 'right' }}>{Number(data.totalMvtDebit).toLocaleString()}</td>
                  <td style={{ textAlign: 'right' }}>{Number(data.totalMvtCredit).toLocaleString()}</td>
                  <td style={{ textAlign: 'right' }}>{Number(data.totalSoldeDebit).toLocaleString()}</td>
                  <td style={{ textAlign: 'right' }}>{Number(data.totalSoldeCredit).toLocaleString()}</td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}
        <div style={{ padding: '8px 16px', fontSize: 11, color: !hasData ? '#888' : equilibre ? '#2e7d32' : '#DC2626', fontWeight: 700, textAlign: 'right' }}>
          {!hasData ? 'Aucune écriture comptable — la balance s\'alimentera automatiquement' : equilibre ? 'Balance équilibrée — Soldes débit = Soldes crédit' : 'ATTENTION : Déséquilibre détecté dans la balance'}
        </div>
      </div>

      {!loading && (
        <div style={{ marginTop: 16 }}>
          <IAAnalysisPanel
            type="balance"
            data={{
              comptes: data.comptes.map(c => ({ numero: c.numero, libelle: c.libelle, classe: c.classe, mvt_debit: c.mvt_debit, mvt_credit: c.mvt_credit, solde_debit: c.solde_debit, solde_credit: c.solde_credit })),
              periode: `${debut} au ${fin}`,
              nb_comptes: data.comptes.length,
              equilibre,
            }}
            buttonLabel="Analyser la balance avec l'IA"
          />
        </div>
      )}
    </div>
  );
}
